/**
 * MODULE SUIVI DE STAGE — « TRANSMETTRE AU CFA » (lot 3.5d-3, E148, E149, E165 ; maquette `#envoiCfa`)
 * =====================================================================================================
 * LA MÊME fenêtre d'envoi que partout (`StageFenetreEnvoi` : De / Répondre à verrouillés, À = le CFA, Cc, Cci,
 * essai, secours messagerie, bilan) — E154 : « il faut que cela soit identique pour ne pas perdre les utilisateurs ».
 * Elle gagne, par ses points d'accroche (`extension`) :
 *   · la ligne « Alternants » : pastilles de formation, dernier envoi, portée (changements / tous), une ligne par
 *     alternant avec son état (nouveau, changé, déjà transmis, à signaler, sans tuteur, sans formation) ;
 *   · le TABLEAU du message, protégé (Q1), et le NOMBRE d'alternants, qui suivent les cases AUSSITÔT ;
 *   · la ligne « Pièce jointe » : le fichier Excel fabriqué PAR LE NAVIGATEUR (D1), « Ne pas joindre », « Télécharger ».
 * Le serveur relit tout à l'envoi (lignes, tableau, nombre) : ce qui est affiché ici n'est qu'un aperçu fidèle.
 * Plusieurs CFA : un message par CFA, choisi avant d'ouvrir (choix A de Bertrand, 2026-10-10).
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useModelesEnvoiStage } from './FenetreModeleCourriel';
import { stageAPI } from '../services/api';
import { fabriquerXlsx, telechargerXlsx } from '../services/cfaExcel';
import StageFenetreEnvoi from './StageFenetreEnvoi';
import StageModale from './StageModale';

const jjmm = (v) => (v ? new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '');
const jour = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '');
const h = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const nomComplet = (l) => [l.nom, l.prenom].filter(Boolean).join(' ');
const message = (e, defaut) => e.response?.data?.detail ? `${e.response.data.error} — ${e.response.data.detail}` : (e.response?.data?.error || defaut);

/** Le tableau du message — la forme protégée que le serveur reconnaît (`envoi-tableau-cfa`), mêmes styles que lui. */
const TH = 'border:1px solid #cbd5e1;padding:4px 8px;background:#f1f5f9;text-align:left';
const TD = 'border:1px solid #cbd5e1;padding:4px 8px';
function tableauHtml(lignes, colonnes) {
    return `<table class="envoi-tableau-cfa" contenteditable="false" style="border-collapse:collapse;font-size:10pt;margin:8px 0">`
        + `<thead><tr>${colonnes.map((c) => `<th style="${TH}">${h(c.libelle)}</th>`).join('')}</tr></thead>`
        + `<tbody>${lignes.map((l) => `<tr>${colonnes.map((c) => `<td style="${TD}">${h(l.valeurs[c.cle] || '')}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
}

/** L'état d'une ligne, tel que la maquette le dessine (colonne « Depuis le … »). */
function Etat({ l }) {
    if (l.etat === 'nouveau') return <span className="badge cfa-badge-nouveau">nouveau</span>;
    if (l.etat === 'change') return <span className="badge cfa-badge-change">changé</span>;
    if (l.etat === 'deja') return <span className="cfa-etat-pale">déjà transmis</span>;
    if (l.etat === 'retire') return <span className="cfa-etat-alerte"><i className="fa-solid fa-triangle-exclamation" /> tuteur retiré — à signaler au CFA</span>;
    if (l.etat === 'sans_tuteur') return <span className="cfa-etat-pale">jamais transmis sans tuteur</span>;
    if (l.etat === 'sans_formation') return <span className="cfa-etat-pale">à corriger dans Étudiants</span>;
    return <span className="cfa-etat-pale">{l.motif || '—'}</span>;
}

export function FenetreTransmissionCfa({ departementId, anneeId, transmission, cfa, onFermer, onTermine, onParametrage }) {
    const groupe = transmission.cfas.find((g) => g.cle === cfa);
    const colonnes = transmission.colonnes;
    // Les lignes de CE CFA, plus celles qui n'ont pas de CFA (sans formation : « à corriger », D5).
    const lignes = useMemo(() => transmission.lignes.filter((l) => l.cfa === cfa || !l.cfa), [transmission, cfa]);
    const formations = useMemo(() => [...new Set(lignes.map((l) => l.formation || 'Sans formation'))], [lignes]);
    const [portee, setPortee] = useState('changements');
    const parDefaut = (p) => new Set(lignes.filter((l) => (p === 'tous' ? l.coche_tous : l.coche_changements)).map((l) => l.etudiant_id));
    const [coches, setCoches] = useState(() => parDefaut('changements'));
    const [masquees, setMasquees] = useState(() => new Set());
    const [brouillon, setBrouillon] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [joindre, setJoindre] = useState(true);
    const texteRef = useRef(null);

    const visible = (l) => !masquees.has(l.formation || 'Sans formation');
    const choisies = useMemo(() => lignes.filter((l) => l.transmissible && coches.has(l.etudiant_id) && visible(l)),   // eslint-disable-line react-hooks/exhaustive-deps
        [lignes, coches, masquees]);

    // Lot MC : « Modèles de courriel… » — le brouillon refait avec les alternants cochés à l'instant
    const { onModeles, fenetre } = useModelesEnvoiStage({ departementId, typeMail: brouillon?.type_mail,
        rePreparer: () => stageAPI.preparerTransmissionCfa(departementId, anneeId, cfa, choisies.map((l) => l.etudiant_id)).then((r) => r.data) });

    // Le brouillon : demandé UNE fois (le modèle, les destinataires, l'objet) ; les cases ne font que changer le tableau.
    useEffect(() => {
        let vivant = true;
        stageAPI.preparerTransmissionCfa(departementId, anneeId, cfa, [...parDefaut('changements')])
            .then((r) => { if (vivant) { setBrouillon(r.data); setJoindre(r.data.fichier?.joindre !== false); } })
            .catch((e) => { if (vivant) setErreur(message(e, 'La préparation du message a échoué')); });
        return () => { vivant = false; };
    }, [departementId, anneeId, cfa]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Q1 : le tableau protégé et le nombre suivent les cases, sans toucher au texte que l'assistante a pu corriger.
    useEffect(() => {
        const zone = texteRef.current;
        if (!zone) return;
        const t = zone.querySelector('table.envoi-tableau-cfa');
        if (t) t.outerHTML = tableauHtml(choisies, colonnes);
        zone.querySelectorAll('.envoi-nombre-cfa').forEach((s) => { s.textContent = String(choisies.length); });
        zone.dispatchEvent(new Event('input', { bubbles: true }));   // la fenêtre garde le texte à jour (htmlRef)
    }, [choisies, colonnes, brouillon]);

    const fichier = useCallback(() => ({
        nom: brouillon?.fichier?.nom || 'affectations_tuteurs.xlsx', feuille: brouillon?.fichier?.feuille || 'Tuteurs',
        entetes: colonnes.map((c) => c.libelle), lignes: choisies.map((l) => colonnes.map((c) => l.valeurs[c.cle] || ''))
    }), [brouillon, colonnes, choisies]);

    const corpsActe = (corps, extra = {}) => ({
        cfa, etudiants: choisies.map((l) => l.etudiant_id), edition: { ...corps.edition, joindre },
        fichier: joindre ? { contenu_base64: fabriquerXlsx(fichier()) } : undefined, ...extra
    });
    const api = {
        envoyer: (dep, corps) => stageAPI.acteTransmissionCfa('envoyer', dep, anneeId, corpsActe(corps)),
        essai: (dep, corps) => stageAPI.acteTransmissionCfa('essai', dep, anneeId, corpsActe(corps, { adresse_essai: corps.adresse_essai })),
        // D3 : le secours compte comme transmis ; le fichier se TÉLÉCHARGE pour être joint à la main (un mailto ne joint rien).
        mailto: (dep, corps) => {
            const c = corpsActe(corps, { fichier: { contenu_base64: fabriquerXlsx(fichier()) } });
            telechargerXlsx(fichier());
            return stageAPI.acteTransmissionCfa('mailto', dep, anneeId, c);
        }
    };

    if (erreur) {
        return (
            <StageModale titre="Transmettre au CFA" icone="fa-paper-plane" onFermer={onFermer}>
                <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>
            </StageModale>
        );
    }
    if (!brouillon) {
        return (
            <div className="stg-modale-fond" role="dialog" aria-modal="true" aria-label="Transmettre au CFA">
                <div className="stg-modale"><div className="stg-modale-body"><div className="stg-attente"><i className="fa-solid fa-circle-notch stg-tourne" /><div>Préparation du message…</div></div></div></div>
            </div>
        );
    }

    const changerPortee = (p) => { setPortee(p); setCoches(parDefaut(p)); };
    const basculer = (l) => setCoches((s) => { const n = new Set(s); if (n.has(l.etudiant_id)) n.delete(l.etudiant_id); else n.add(l.etudiant_id); return n; });
    const basculerFormation = (f) => setMasquees((s) => { const n = new Set(s); if (n.has(f)) n.delete(f); else n.add(f); return n; });
    const dernier = groupe?.dernier_envoi;
    const nbPortee = (p) => lignes.filter((l) => visible(l) && (p === 'tous' ? l.coche_tous : l.coche_changements)).length;

    const alternants = (
        <div className="fe-ligne">
            <label>Alternants</label>
            <div className="fe-champ cfa-envoi">
                <div className="cfa-envoi-haut">
                    {formations.map((f) => (
                        <button key={f} type="button" className={'cfa-pastille' + (masquees.has(f) ? '' : ' actif')} onClick={() => basculerFormation(f)}
                                title={masquees.has(f) ? 'Afficher cette formation' : 'Masquer cette formation (ses alternants ne sont pas transmis)'}>{f}</button>
                    ))}
                    <span className="cfa-dernier">
                        <i className="fa-solid fa-clock-rotate-left" />{' '}
                        {dernier ? <>Dernier envoi : <b>{jour(dernier.le)}</b>{dernier.par ? ` par ${dernier.par}` : ''}{dernier.mode === 'mailto' ? ' (préparé dans la messagerie)' : ''}</> : 'Jamais transmis à ce CFA'}
                    </span>
                </div>
                <div className="cfa-portee">
                    <label><input type="radio" name="cfaPortee" checked={portee === 'changements'} onChange={() => changerPortee('changements')} />{' '}
                        <b>{dernier ? `Seulement les changements depuis le ${jjmm(dernier.le)}` : 'Seulement les nouveaux et les changés'}</b> ({nbPortee('changements')})</label>
                    <label><input type="radio" name="cfaPortee" checked={portee === 'tous'} onChange={() => changerPortee('tous')} />{' '}
                        Tous les alternants ({nbPortee('tous')})</label>
                </div>
                <div className="cfa-envoi-table-cadre">
                    <table className="cfa-envoi-table">
                        <thead><tr><th /><th>Alternant</th><th>Formation</th><th>Tuteur pédagogique</th><th>{dernier ? `Depuis le ${jjmm(dernier.le)}` : 'État'}</th></tr></thead>
                        <tbody>
                            {lignes.filter(visible).map((l) => {
                                const coche = l.transmissible && coches.has(l.etudiant_id);
                                const alerte = ['retire', 'sans_tuteur'].includes(l.etat);
                                return (
                                    <tr key={l.etudiant_id} data-etudiant={l.etudiant_id}
                                        className={(alerte ? 'cfa-ligne-alerte' : '') + (!l.transmissible && !alerte ? ' cfa-ligne-grise' : '') + (l.transmissible && !coche ? ' cfa-ligne-pale' : '')}>
                                        <td><input type="checkbox" checked={coche} disabled={!l.transmissible} onChange={() => basculer(l)}
                                                   title={l.transmissible ? undefined : l.motif} aria-label={`Transmettre ${nomComplet(l)}`} /></td>
                                        <td>{coche ? <b>{nomComplet(l)}</b> : nomComplet(l)}</td>
                                        <td>{l.formation || '—'}{l.fc && <span className="badge cfa-badge-fc" title="Formation continue : décochée par défaut — à vous de choisir (D2)">FC</span>}</td>
                                        <td>{l.tuteur
                                            ? <>{l.tuteur}{l.ancien_tuteur && l.etat === 'change' && <span className="cfa-avant"> (avant : {l.ancien_tuteur})</span>}</>
                                            : <span className="cfa-etat-alerte"><i className="fa-solid fa-triangle-exclamation" /> sans tuteur{l.ancien_tuteur ? ` (avant : ${l.ancien_tuteur})` : ''}</span>}</td>
                                        <td><Etat l={l} /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="cfa-envoi-note">
                    Cocher / décocher met à jour le tableau du message et le fichier joint. Colonnes et CFA :{' '}
                    <button type="button" className="cfa-lien" onClick={onParametrage}>Tuteurs → Paramétrage → CFA</button>.
                </div>
            </div>
        </div>
    );

    const f = fichier();
    const pieceJointe = (
        <div className="fe-ligne">
            <label>Pièce&nbsp;jointe</label>
            <div className="fe-champ">
                {joindre ? (
                    <span className="fe-chip cfa-pj">
                        <i className="fa-solid fa-file-excel" style={{ color: '#16a34a' }} /> <span>{f.nom}</span>
                        <span className="cfa-pj-detail">· {f.lignes.length} ligne{f.lignes.length > 1 ? 's' : ''} · {f.entetes.length} colonnes</span>
                        <i className="fa-solid fa-xmark" title="Ne pas joindre le fichier à cet envoi" onClick={() => setJoindre(false)} />
                    </span>
                ) : (
                    <span className="cfa-pj-absente">Aucune pièce jointe pour cet envoi — <button type="button" className="cfa-lien" onClick={() => setJoindre(true)}>joindre le fichier Excel</button></span>
                )}
                <div className="fe-aide" style={{ marginTop: 3 }}>
                    Fabriqué au moment de l'envoi d'après les colonnes du Paramétrage ; non conservé (le journal garde son empreinte).{' '}
                    <button type="button" className="cfa-lien" disabled={!f.lignes.length} onClick={() => telechargerXlsx(f)}>Télécharger</button>
                </div>
            </div>
        </div>
    );

    const extension = {
        avantObjet: alternants, apresObjet: pieceJointe, texteRef,
        bloquer: () => (choisies.length ? null : 'Cochez au moins un alternant.'),
        textes: {
            essai: (d) => <>Essai envoyé à <b>{d.adresse}</b>{d.redirige_vers ? ` (redirigé vers ${d.redirige_vers})` : ''}{d.piece_jointe ? ', avec le fichier Excel' : ''}. <b>Rien n'est compté comme transmis</b> — vérifiez votre boîte, puis envoyez pour de bon.</>,
            secours: (d) => <>Message <b>préparé</b> dans votre messagerie : le tableau y est en texte, et le fichier <b>{d.fichier_nom}</b> vient d'être téléchargé — <b>joignez-le à la main</b>. Comptés comme transmis : {d.nb_alternants} alternant{d.nb_alternants > 1 ? 's' : ''}.{d.trop_long ? ' ⚠️ Le message est très long : votre messagerie pourrait le couper.' : ''}</>,
            bilan: (b) => <><i className="fa-solid fa-building-columns" /> Transmis au CFA : <b>{b.nb_alternants}</b> alternant{b.nb_alternants > 1 ? 's' : ''}{b.piece_jointe ? <>, fichier <b>{b.piece_jointe.nom}</b> joint (empreinte gardée au journal)</> : ', sans pièce jointe'}.</>
        }
    };

    return <><StageFenetreEnvoi brouillon={brouillon} departementId={departementId} onFermer={onFermer} onTermine={onTermine} api={api} extension={extension} onModeles={onModeles} />{fenetre}</>;
}

/**
 * Le bouton « Transmettre au CFA » : lit les lignes, puis ouvre la fenêtre du CFA — ou, s'il y en a plusieurs,
 * propose d'abord le choix (un message par CFA, choix A).
 */
export default function StageTransmissionCfa({ departementId, anneeId, modifiable, lectureSeule, onTermine, onParametrage }) {
    const [etat, setEtat] = useState(null);       // { transmission, choix: true } | { transmission, cfa } | { erreur }
    const [lecture, setLecture] = useState(false);
    const ouvrir = async () => {
        setLecture(true);
        try {
            const r = await stageAPI.getTransmissionCfa(departementId, anneeId);
            const t = r.data;
            if (!t.cfas.length) {
                setEtat({ erreur: t.lignes.length
                    ? 'Aucun CFA destinataire n’est réglé. Indiquez son nom et son adresse dans Tuteurs → Paramétrage → CFA, puis recommencez.'
                    : 'Aucun alternant (apprenti ou formation continue) n’est inscrit cette année.' , parametrage: !!t.lignes.length });
            } else if (t.cfas.length === 1) setEtat({ transmission: t, cfa: t.cfas[0].cle });
            else setEtat({ transmission: t, choix: true });
        } catch (e) { setEtat({ erreur: message(e, 'Lecture des alternants impossible') }); }
        finally { setLecture(false); }
    };
    const fermer = () => setEtat(null);
    return (
        <>
            <button type="button" className="btn btn-green btn-sm" onClick={ouvrir} disabled={!modifiable || lecture}
                    title={!modifiable ? lectureSeule : "Transmettre au CFA l'affectation des tuteurs des alternants (le suivi des apprentis est confié au CFA — Paramétrage → CFA)"}>
                <i className={'fa-solid ' + (lecture ? 'fa-circle-notch stg-tourne' : 'fa-paper-plane')} /> Transmettre au CFA
            </button>
            {etat?.erreur && (
                <StageModale titre="Transmettre au CFA" icone="fa-paper-plane" onFermer={fermer}
                             libelleValider={etat.parametrage ? 'Aller au Paramétrage' : undefined}
                             onValider={etat.parametrage ? () => { fermer(); onParametrage(); } : undefined}>
                    <p>{etat.erreur}</p>
                </StageModale>
            )}
            {etat?.choix && (
                <StageModale titre="Transmettre au CFA — quel CFA ?" icone="fa-building-columns" onFermer={fermer}>
                    <p style={{ marginTop: 0 }}>Vos formations dépendent de <b>{etat.transmission.cfas.length} CFA</b> : un message par CFA. Choisissez celui à qui écrire.</p>
                    <div className="cfa-choix">
                        {etat.transmission.cfas.map((g) => (
                            <button key={g.cle} type="button" className="cfa-choix-btn" onClick={() => setEtat({ transmission: etat.transmission, cfa: g.cle })}>
                                <b>{g.nom}</b> <span>{g.email}</span>
                                <span className="cfa-choix-detail">{g.nb_changements} changement{g.nb_changements > 1 ? 's' : ''} · {g.dernier_envoi ? `dernier envoi le ${jour(g.dernier_envoi.le)}` : 'jamais transmis'}</span>
                            </button>
                        ))}
                    </div>
                </StageModale>
            )}
            {etat?.cfa && (
                <FenetreTransmissionCfa departementId={departementId} anneeId={anneeId} transmission={etat.transmission} cfa={etat.cfa}
                                        onFermer={fermer} onTermine={(fait) => { if (fait && onTermine) onTermine(); }}
                                        onParametrage={() => { fermer(); onParametrage(); }} />
            )}
        </>
    );
}
